import type { RouteLocationNormalizedLoaded } from 'vue-router'
import { useRouter } from 'vue-router'

import { computed, type Ref, ref, watch } from 'vue'
import { useTranslator } from '@/i18n'
import { useInitialState } from '@/app'

import type { InitQuery, InitQueryVariables } from '@/graphql/api'
import { useAutologinMutation, useInitQuery } from '@/graphql/api'
import { bench } from '@/helpers/bench'
import { useMenus } from '@/plugins/menu'
import {
    defaultRegion,
    getRoutingRegion,
    useRegionPlugin,
} from '@/plugins/region'
import { useAuth } from '@/modules/auth'
import { useRingBuilderDataStore } from '@/composables/useRingBuilder'
import { useRedirect } from 'wucdbm-vite-vue-ssr'
import { useRouteQuery } from '@vueuse/router'
import { useIsAdmin } from '@/components/app/isAdmin'
import { useCurrencyInput } from '@/plugins/currency-input.ts'
import { trackGtmEvent } from '@/composables/app/gtm/event.ts'

export async function useInit(
    currentRoute: RouteLocationNormalizedLoaded,
    hasRegionSet: Ref<boolean>
) {
    const { locale, setMessages } = useTranslator()
    const { regions, regionCode, region } = useRegionPlugin()
    const currency = useCurrencyInput()
    const {
        setTopBar,
        setPreHeader,
        setMainMenu,
        setFooter,
        setPaymentIcons,
        setSocialNetworks,
        setAvatarStack,
    } = useMenus()
    const { setUser } = useAuth()
    const isAdmin = useIsAdmin()
    const router = useRouter()
    const autologinMutation = useAutologinMutation()
    const authToken = useRouteQuery('authToken')

    const ringBuilderDataStore = useRingBuilderDataStore()
    const redirect = useRedirect()

    watch(region, (newRegion) => {
        if (newRegion && !newRegion.isActive) {
            const route = router.resolve({
                name: router.currentRoute.value.name,
                params: {
                    ...router.currentRoute.value.params,
                    region: getRoutingRegion(defaultRegion),
                },
            })
            redirect(route.href, 301)
        }
    })

    const pauseInitQuery = ref(
        typeof currentRoute.query['authToken'] === 'string'
    )

    const renderBench = bench()
    const initQueryResponse = useInitQuery({
        variables: computed((): InitQueryVariables => {
            return {
                locale: locale.value,
                currency: currency.value,
                region: regionCode.value,
                fetchRegionSuggest: !hasRegionSet.value,
                isConsultant: isAdmin.value,
            }
        }),
        pause: () => pauseInitQuery.value,
    })

    const { operation, data, error } = initQueryResponse
    const initialState = useInitialState()
    const apiState = initialState.api
    const operationState = apiState[operation.value?.key || '']
    const initData = ref<InitQuery | undefined>(undefined)

    async function processInitData(data: InitQuery): Promise<void> {
        // processTranslations(data.language?.translations || [], translator, ref(region))
        setTopBar(data.topBar)
        setPreHeader(data.preHeader)
        setMainMenu(data.mainMenu)
        setFooter(data.footer)
        setPaymentIcons(data.paymentIcons)
        setSocialNetworks(data.socialNetworks)
        setAvatarStack(data.avatarStack)
        setUser(data.me)
        ringBuilderDataStore.setState(data.ringBuilder)
        data.regions.forEach((item) => {
            regions.value[item.code] = item
        })
        if (data.language) {
            // За бъдещите поколения
            // ДА НЕ СЕ МЕСТИ НАД РИНГ БИЛДЪРА
            // ЧЕ СТАВА МНОГО ЛОШО
            // ЗА ОСТАНАЛИТЕ, НЕ Е СЪВСЕМ СИГУРНО
            await setMessages(data.language.locale, data.language.translations)
        }
    }

    watch(data, async (data) => {
        if (!data) {
            return
        }

        // This watcher will set VAT/Rates a second time during INIT in a browser
        // Nothing we can do, but we NEED to set VAT/Rates in the promises
        // Due to the fact that SSR will not react to this
        await processInitData(data)
    })

    let processed = false
    if (operationState?.data) {
        initData.value = JSON.parse(operationState.data) as InitQuery

        if (initData.value) {
            await processInitData(initData.value)
        }

        processed = true
    }

    const showRegionModal = computed(() => {
        if (hasRegionSet.value) {
            return false
        }

        const regionSuggest = initData.value?.regionSuggest

        if (!regionSuggest) {
            return false
        }

        return regionSuggest.code !== regionCode.value
    })

    // eslint-disable-next-line vue-composable/composable-placement
    trackGtmEvent('site_version', { 'version': 'v2' })

    if (typeof authToken.value === 'string') {
        const response = await autologinMutation.executeMutation({
            authToken: authToken.value,
            isConsultant: isAdmin.value,
        })

        if (response.data?.autologin.__typename === 'LoginResponseSuccess') {
            setUser(response.data?.autologin.user)
        }

        // TODO remove logic from RegionModal and uncomment this
        // after teleport/suspense/navigation bug is fixed
        // https://github.com/vuejs/core/issues/7966
        // authToken.value = null

        pauseInitQuery.value = false
    }

    await initQueryResponse

    renderBench.end('InitQuery')

    initData.value = data.value

    if (!processed && data.value) {
        await processInitData(data.value)
    }

    return {
        error,
        initQueryResponse,
        hasRegionSet,
        showRegionModal,
    }
}
